import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Format price with thousands separator and decimals
export function formatPrice(price: number | string, options?: {
  currency?: string;
  decimals?: number;
  locale?: string;
}): string {
  const {
    currency = 'USD',
    decimals = 2,
    locale = 'en-US'
  } = options || {};

  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  if (isNaN(numPrice)) return '$0.00';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(numPrice);
}

// Extract city name from full address
// Example: "1200 Broadway St, Ann Arbor, MI 48105" -> "Ann Arbor"
export function extractCityFromAddress(fullAddress?: string): string {
  if (!fullAddress) return '';
  
  // Split by comma and trim spaces
  const parts = fullAddress.split(',').map(part => part.trim());
  
  // Typically, the city is the second-to-last part before state/zip
  // Format: "Street, City, State Zip"
  if (parts.length >= 2) {
    return parts[parts.length - 2];
  }
  
  return fullAddress;
}

// Calculate distance between two coordinates using Haversine formula
// Returns distance in miles
export function calculateDistance(
  lat1: number | null | undefined,
  lon1: number | null | undefined,
  lat2: number | null | undefined,
  lon2: number | null | undefined
): number | null {
  // Return null if any coordinate is missing
  if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) {
    return null;
  }

  const R = 3959; // Earth's radius in miles (use 6371 for km)
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return distance;
}

// Convert degrees to radians
function toRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}

// Format distance for display
export function formatDistance(miles: number | null): string {
  if (miles === null) return '';
  
  if (miles < 0.1) {
    return '< 0.1 mi';
  } else if (miles < 1) {
    return `${miles.toFixed(1)} mi`;
  } else if (miles < 10) {
    return `${miles.toFixed(1)} mi`;
  } else {
    return `${Math.round(miles)} mi`;
  }
}
